import { forwardRef } from '@chakra-ui/react';
import {
  UiBox,
  type UiBoxProps,
  UiStack,
  uiStyles,
  UiText
} from '@/lib/ui';
import { type FieldMetadataSelect, type FieldMetadata } from '@/base/FormBuilder/Field';
import BaseFormInputField from '@/base/Form/InputField';
import BaseFormSelectField, { type Option } from '@/base/Form/SelectField';
import BaseFormPhoneField from '@/base/Form/PhoneField';
import countries from '@/app/countries.json';

const countryOptions = countries.map((countryName) => { return { value: countryName, label: countryName }; });

export interface GridItemProps extends UiBoxProps {
  fieldMetadata: FieldMetadata
}

const createSelectOptions = (fieldMetadata: FieldMetadataSelect): Option[] => {
  const options: Option[] = [];
  if (fieldMetadata.options) {
    for (const opt of fieldMetadata.options.split('\n')) {
      options.push({ value: opt, label: opt });
    }
  }
  return options;
};

const GridItem = forwardRef<GridItemProps, 'div'>(({
  fieldMetadata,
  // children,
  ...props
}, ref) => {
  return (
    <>
      <UiBox
        ref={ref}
        // p={4}
        // px={8}
        py={4}
        bgColor={'transparent'}
        borderRadius={uiStyles.borderRadius}
        // {...uiStyles.hover}
        _hover={{
          cursor: 'pointer'
        }}
        {...props}
      >
        {/* {children} */}
        {fieldMetadata.type === 'input' && (
          <UiBox data-testid={'input-field'}>
            <BaseFormInputField
              name={fieldMetadata.id}
              label={fieldMetadata.label}
              helperText={fieldMetadata.helpText}
              isRequired={fieldMetadata.isRequired}
              layout={'stack'}
            />
          </UiBox>
        )}
        {fieldMetadata.type === 'select' && fieldMetadata.optionType === 'country' && (
          <UiBox data-testid={'country-select'}>
            <BaseFormSelectField
              name={fieldMetadata.id}
              label={fieldMetadata.label}
              helperText={fieldMetadata.helpText}
              options={countryOptions}
              isRequired={fieldMetadata.isRequired}
              layout={'stack'}
              data-testid={'country-select'}
            />
          </UiBox>
        )}
        {fieldMetadata.type === 'select' && fieldMetadata.optionType === 'custom' && (
          <UiBox data-testid={'custom-select'}>
            <BaseFormSelectField
              name={fieldMetadata.id}
              label={fieldMetadata.label}
              helperText={fieldMetadata.helpText}
              isMultiple={fieldMetadata.isMultiple}
              maximumOptionsSelectable={fieldMetadata.maximumOptionsSelectable}
              options={createSelectOptions(fieldMetadata)}
              isRequired={fieldMetadata.isRequired}
              layout={'stack'}
              data-testid={'custom-select'}
            />
          </UiBox>
        )}
        {fieldMetadata.type === 'date' && (
          <UiBox data-testid={'date-field'}>
            <BaseFormInputField
              name={fieldMetadata.id}
              label={fieldMetadata.label}
              type="date"
              helperText={fieldMetadata.helpText}
              isRequired={fieldMetadata.isRequired}
              layout={'stack'}
              data-testid={'date-field'}
            />
          </UiBox>
        )}
        {fieldMetadata.type === 'phone' && (
          <UiBox data-testid={'phone-field'}>
            <BaseFormPhoneField
              name={fieldMetadata.id}
              label={fieldMetadata.label}
              helperText={fieldMetadata.helpText}
              isRequired={fieldMetadata.isRequired}
              layout={'stack'}
              data-testid={'phone-field'}
            />
          </UiBox>
        )}
        {fieldMetadata.type === 'descriptionBlock' && (
          <UiStack p={4} px={{ base: 4, lg: 6 }} bgColor={'#fff'} borderRadius={uiStyles.borderRadius} data-testid={'description-block'}>
            <UiText>{fieldMetadata.description}</UiText>
          </UiStack>
        )}
        {fieldMetadata.type === 'sectionBreak' && (
          <UiStack
            spacing={0}
            borderBottomWidth={1}
            borderBottomColor={'gray.300'}
            borderBottomStyle={'solid'}
            pb={4}
            data-testid={'section-break'}
          >
            <UiText variant={'title'}>{fieldMetadata.label}</UiText>
            {!!fieldMetadata.description && (
              <UiText variant={'body1'} color={'text.secondary'}>{fieldMetadata.description}</UiText>
            )}
          </UiStack>
        )}
      </UiBox>
    </>
  );
});
export default GridItem;
